import React from 'react';
import { X } from 'lucide-react';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import FilterRulesEditor from './FilterRulesEditor';
import EvalSettingsFields from './EvalSettingsFields';
import SamplingField from './SamplingField';
import { Section } from './formParts';
import { TARGETS } from 'features/evals3/templates';

/**
 * The eval itself, as opposed to one of its steps: name, description, what
 * it scores, the filters that decide whether an event is scored at all
 * (which agent is one of them), and the eval-level settings (self-improving,
 * backfill).
 *
 * name / description / enabled save in place (no version). Everything else
 * here is part of the frozen version, like the graph, and goes out with Save.
 */
export default function EvalSettingsPanel({
  lineage, draft, onLineage, onDraft, onClose, teamId = null, integrations = [], modelsFor = () => [], schema, scopes = null, readOnly = false, canEnable = true, editorMode = null, onEditorMode = null, canSimple = false,
}) {
  const target = TARGETS.find((t) => t.value === lineage.target);
  const fields = scopes ? null : (schema?.groups || []).flatMap((g) => g.fields).filter((f) => f.type !== 'list');
  return (
    <aside className="relative flex h-full w-[max(480px,min(560px,calc(100vw_-_220px)))] shrink-0 flex-col border-l border-[color:var(--border-default)] bg-[color:var(--surface-base)]" style={{ fontFamily: 'Inter, sans-serif' }} aria-label="Eval settings">
      {/* The step panel's header: same height, title size and close button, so the two panels swap without a jump. */}
      <div className="flex h-[55px] shrink-0 items-center justify-between border-b border-[color:var(--border-default)] px-3">
        <div className="text-[13.5px] font-semibold text-[color:var(--text-primary)]">Eval settings</div>
        <button type="button" onClick={onClose} aria-label="Close" title="Close"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"><X className="h-4 w-4" /></button>
      </div>
      <fieldset disabled={readOnly} className="min-h-0 min-w-0 flex-1 overflow-y-auto px-3 py-3.5" style={{ border: 0, margin: 0 }}>
        {/* The switch that decides whether any of the rest happens. It lives
            here rather than in the toolbar: it is a setting, and the toolbar
            is for what you are doing right now. Not versioned. It saves the
            moment it changes. */}
        <Section title="Enabled" hint={canEnable ? 'Off means nothing new is scored. Past results stay.' : 'Add a first step and set it as production before this can be turned on.'}>
          <SegmentedTabs ariaLabel="Enabled" value={lineage.enabled ? 'on' : 'off'}
            onChange={(v) => { if (canEnable || v === 'off') onLineage({ enabled: v === 'on' }); }}
            options={[{ value: 'on', label: 'On', hint: canEnable ? undefined : 'Needs a first step' }, { value: 'off', label: 'Off' }]} />
        </Section>
        {/* Only a one-check eval has two editors to choose between. Once steps route to each other there is nothing to choose, so nothing is shown. */}
        {onEditorMode && (editorMode === 'simple' || canSimple) && (
          <Section title="Editor" hint="Simple is one check and a rule. Triage is steps that route to each other, on a canvas.">
            <SegmentedTabs ariaLabel="Editor" value={editorMode} onChange={onEditorMode}
              options={[{ value: 'simple', label: 'Simple' }, { value: 'triage', label: 'Triage' }]} />
          </Section>
        )}
        <Section title="Name">
          <input value={lineage.name || ''} maxLength={120} aria-label="Eval name setting" onChange={(e) => onLineage({ name: e.target.value })} className="input w-full" />
        </Section>
        <Section title="Description" hint="what a pass means, in a sentence">
          <textarea value={lineage.description || ''} rows={3} aria-label="Eval description" onChange={(e) => onLineage({ description: e.target.value })} className="input min-h-[70px] w-full" />
        </Section>
        <Section title="Scores each" hint="fixed once created">
          <div className="flex h-8 items-center rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-2.5 text-[12px]">
            <span className="font-medium text-[color:var(--text-primary)]">{target?.label || lineage.target}</span>
          </div>
        </Section>
        <Section title="Filters" hint="which events get scored; a non-match makes no run and costs nothing">
          <FilterRulesEditor value={draft.applies_when || { all: [] }} target={lineage.target} scopes={scopes} fields={fields} readOnly={readOnly} teamId={teamId}
            onChange={(f) => onDraft({ applies_when: (f.all && f.all.length === 0) || (f.any && f.any.length === 0) ? null : f })} />
        </Section>
        <Section title="Sampling" hint="how much of the matching traffic is scored; saves the moment it changes, never a version">
          <SamplingField target={lineage.target} value={lineage.sample_pct ?? 100} readOnly={readOnly} onChange={(pct) => onLineage({ sample_pct: pct })} />
        </Section>
        <EvalSettingsFields target={lineage.target} value={draft} onChange={onDraft} integrations={integrations} modelsFor={modelsFor} readOnly={readOnly} teamId={teamId} lineageId={lineage.id} />
      </fieldset>
    </aside>
  );
}
